/**
 * Day-ahead electricity prices as a Tilez public server gives them (docs/specs/public-services.md
 * SV-11 to SV-14), and the zones it gives them for: the contract between the server's prices
 * service and the "Tilez energy prices" driver.
 */

/**
 * The bidding zones whose prices Energy-Charts licenses as CC BY 4.0 (from Bundesnetzagentur |
 * SMARD.de), and their names. Its other zones are "for private and internal use only", so a public
 * server does not give them out (SV-11, QSV-15).
 */
export const ENERGY_PRICE_ZONES = {
    AT: 'Austria',
    BE: 'Belgium',
    CH: 'Switzerland',
    CZ: 'Czech Republic',
    'DE-LU': 'Germany and Luxembourg',
    'DE-AT-LU': 'Germany, Austria and Luxembourg',
    DK1: 'Denmark west',
    DK2: 'Denmark east',
    FR: 'France',
    HU: 'Hungary',
    'IT-North': 'Italy north',
    NL: 'Netherlands',
    NO2: 'Norway south-west',
    PL: 'Poland',
    SE4: 'Sweden south',
    SI: 'Slovenia',
} as const;

export type EnergyPriceZone = keyof typeof ENERGY_PRICE_ZONES;

export const isEnergyPriceZone = (value: unknown) : value is EnergyPriceZone => typeof value === 'string' && Object.hasOwn(ENERGY_PRICE_ZONES, value);

/** "Today" and "tomorrow" are the market's delivery days, midnight to midnight here (SV-13). */
export const ENERGY_MARKET_TIME_ZONE = 'Europe/Brussels';

/** One slot of the market: from `start` to `end` (ms), at `value` in the answer's unit. */
export interface PriceSlot {
    readonly start: number;
    readonly end: number;
    readonly value: number;
}

/** `GET /api/services/prices?zone=<zone>`: today's slots, and tomorrow's once they are published. */
export interface DayAheadPrices {
    readonly zone: EnergyPriceZone;
    /** `EUR/MWh`, as the market gives it. */
    readonly unit: string;
    /** The source's licence, to be shown with the prices (CC BY 4.0). */
    readonly license: string;
    /** In time order. */
    readonly slots: readonly PriceSlot[];
}
